import React, { PropsWithChildren, RefObject } from 'react';
import {
  StyleSheet,
  Platform,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import { Menu as RNMM } from 'react-native-material-menu';
import { withHapticFeedback } from 'lib/withHapticFeedback';
import { theme } from 'lib/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import reactStringReplace from 'react-string-replace';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { useDispatch } from 'react-redux';
import { createAction } from 'typesafe-actions';
import {
  Button,
  BUTTON_COMPACT_HEIGHT,
  BUTTON_HEIGHT,
  ButtonProps,
} from './Button';
import { Icon, ICONS } from './Icon';
import { IconButton, IconButtonProps } from './IconButton';
import { SeparatorHorizontal } from './SeparatorHorizontal';
import { ViewWithSeparators } from './ViewWithSeparators';
import { Txt, TxtProps } from './Txt';
import { Pressable } from './Pressable';

type MenuRef = {
  close: () => void;
};

type DefinedMenuOption = {
  key?: string;
  text: string;
  subText?: string;
  horizontalText?: boolean;
  onPress?: () => void;
  icon?: ICONS;
  iconColor?: string;
  leftIcon?: ICONS;
  leftIconColor?: string;
  textColor?: string;
  destructive?: boolean;
  textNumberOfLines?: number;
  textSize?: TxtProps['size'];
  keepMenuOpenOnPress?: boolean;
};

export type MenuOption = DefinedMenuOption | '-' | string | React.ReactElement;

type Props = {
  visible?: boolean;
  options: MenuOption[];
  onDismiss?: () => void;
  width?: number;
  marginTop?: number;

  x?: number | undefined;
  y?: number | undefined;
  parentHeight?: number | undefined;
  parentWidth?: number | undefined;
  parentX?: number | undefined;
  parentY?: number | undefined;
  height?: number;
  offsetX?: number;
  disabled?: boolean;
  ref?: RefObject<MenuRef>;
};

type PropsWithAnchor = {
  anchor?: React.ReactElement;
  buttonConfig?: never;
  iconButtonConfig?: never;
};

type PropsWithIconButton = {
  anchor?: never;
  buttonConfig?: never;
  iconButtonConfig?: Omit<IconButtonProps, 'onPress' | 'onPressIn'>;
};

type PropsWithButton = {
  anchor?: never;
  buttonConfig?: Omit<ButtonProps, 'onPress' | 'onPressIn'>;
  iconButtonConfig?: never;
};

const typeGuard = <T extends Record<string, unknown>>(
  toBeDetermined: unknown,
): toBeDetermined is T => !(toBeDetermined as T).type;

export type MenuProps = Props &
  (PropsWithIconButton | PropsWithButton | PropsWithAnchor);

const eventCloseMenus = createAction('doCloseMenus')();

export const Menu = ({
  anchor,
  buttonConfig,
  iconButtonConfig,
  onDismiss,
  options,
  visible,
  width = 250,
  marginTop = 0,
  x,
  y,
  parentHeight,
  parentWidth,
  parentX,
  parentY,
  height,
  offsetX,
  disabled,
  ref,
}: PropsWithChildren<MenuProps>) => {
  const dispatch = useDispatch();
  const [internalVisible, setInternalVisible] = React.useState<boolean>(false);
  const menuRef = React.useRef<RNMM>(null);
  const allowInteraction = React.useRef<boolean>(false);
  const allowInteractionTimeout =
    React.useRef<ReturnType<typeof setTimeout>>(null);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [menuDims, setMenuDims] = React.useState({ width: 0, height: 0 });
  const { top: topSafeArea } = useSafeAreaInsets();
  // const centerX = centered ? windowWidth / 2 - width / 2 : undefined;
  let customX: number | undefined;
  let customY: number | undefined;
  let customRTL: boolean = false;
  let customBTT: boolean = false;

  const allowedMaxHeight = Math.min(
    height || 9999,
    windowHeight - (topSafeArea + 80),
  );

  // Custom X
  if (x !== undefined) {
    customX = x;
  } else if (parentX !== undefined && parentWidth !== undefined) {
    customX = parentX + parentWidth / 2 - width / 2;
  }
  if (customX && customX + width > windowWidth) {
    customRTL = true;
  }

  // Custom Y
  if (y !== undefined) {
    customY = y;
  } else if (parentY !== undefined && parentHeight !== undefined) {
    customY = parentY + parentHeight;
  }
  if (customY && customY + menuDims.height + 20 > windowHeight) {
    customBTT = true;
    if (y) {
      customY = y - marginTop * 2;
    } else if (parentY) {
      customY = parentY - marginTop * 2;
    }
  }
  if (customBTT && customY && customY - menuDims.height < 40 + topSafeArea) {
    customY = topSafeArea + 40 + menuDims.height;
  }

  const internalAnchor: React.ReactElement | null =
    anchor ||
    (buttonConfig ? (
      <Button
        {...buttonConfig}
        disabled={disabled || buttonConfig.disabled}
        onPress={
          disabled
            ? undefined
            : withHapticFeedback(() => setInternalVisible(true))
        }
      />
    ) : iconButtonConfig ? (
      <IconButton
        active={iconButtonConfig.active}
        iconSize={iconButtonConfig.iconSize}
        color={iconButtonConfig.color}
        disabled={disabled || iconButtonConfig.disabled}
        imageStyle={iconButtonConfig.imageStyle}
        icon={iconButtonConfig.icon}
        marginHorizontal={iconButtonConfig.marginHorizontal}
        size={iconButtonConfig.size}
        style={iconButtonConfig.style}
        borderRadius={iconButtonConfig.borderRadius}
        hoverColor={iconButtonConfig.hoverColor}
        hoveredStyle={iconButtonConfig.hoveredStyle}
        pressedStyle={iconButtonConfig.pressedStyle}
        pressedColor={iconButtonConfig.pressedColor}
        showBadge={iconButtonConfig.showBadge}
        onPress={
          disabled
            ? undefined
            : withHapticFeedback(() => setInternalVisible(true))
        }
      />
    ) : null);

  const handleDismiss = React.useCallback(() => {
    if (allowInteraction.current) {
      allowInteraction.current = false;
      setInternalVisible(false);
      onDismiss?.();
    }
  }, [onDismiss]);

  React.useImperativeHandle(
    ref,
    () => ({
      close: handleDismiss,
    }),
    [handleDismiss],
  );

  const menuMarginTop =
    marginTop !== 0
      ? marginTop
      : (buttonConfig?.compact ? BUTTON_COMPACT_HEIGHT : BUTTON_HEIGHT) + 6;

  const isVisible = !!visible || internalVisible;

  const handleContextPress = React.useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      handleDismiss();
    },
    [handleDismiss],
  );

  React.useEffect(() => {
    if (Platform.OS === 'web' && isVisible) {
      document.addEventListener('contextmenu', handleContextPress);
    }
    return () => {
      if (Platform.OS === 'web' && isVisible) {
        document.removeEventListener('contextmenu', handleContextPress);
      }
    };
  }, [handleContextPress, isVisible]);

  React.useEffect(() => {
    const events: number[] = [];
    if (allowInteractionTimeout.current) {
      clearTimeout(allowInteractionTimeout.current);
      allowInteractionTimeout.current = null;
    }
    if (isVisible) {
      allowInteractionTimeout.current = setTimeout(() => {
        allowInteraction.current = true;
        allowInteractionTimeout.current = null;
      }, 300);
      doAddEvent(eventCloseMenus, () => {
        handleDismiss();
      });
    }
    return () => {
      if (allowInteractionTimeout.current) {
        clearTimeout(allowInteractionTimeout.current);
        allowInteractionTimeout.current = null;
      }
      if (events.length) {
        doRemoveEvents(events);
      }
    };
  }, [handleDismiss, isVisible]);

  let hasOptionAbove = false;
  const bold = /\*(.*?)\*/gm;

  return (
    <RNMM
      visible={isVisible}
      onRequestClose={handleDismiss}
      anchor={internalAnchor}
      animationDuration={190}
      horizontalDirection={customRTL ? 'rtl' : undefined}
      verticalDirection={customBTT ? 'btt' : undefined}
      ref={menuRef}
      style={[
        styles.menu,
        {
          marginVertical: menuMarginTop,
          marginLeft: offsetX || 0,
        },
        customX !== undefined && customY !== undefined
          ? { left: customX, top: customY }
          : undefined,
        disabled ? styles.menuDisabled : undefined,
      ]}
      onMenuLayout={(dims: { width: number; height: number }) =>
        setMenuDims({ width: dims.width, height: dims.height })
      }
    >
      <View style={{ borderRadius: 10, overflow: 'hidden' }}>
        <ScrollView
          style={{
            maxHeight: allowedMaxHeight,
            pointerEvents: 'auto',
          }}
        >
          <ViewWithSeparators>
            {options.map((option, index, array) => {
              if (option === '-') {
                const nextOption = array[index + 1];
                if (hasOptionAbove && nextOption && nextOption !== '-') {
                  hasOptionAbove = false;
                  return (
                    <SeparatorHorizontal
                      key={Math.random()}
                      style={{
                        backgroundColor:
                          Platform.OS === 'ios' ? '#999' : '#ddd',
                        height:
                          Platform.OS === 'ios' ? StyleSheet.hairlineWidth : 1,
                        marginTop: -(Platform.OS === 'ios'
                          ? StyleSheet.hairlineWidth
                          : 1),
                        marginBottom: -(Platform.OS === 'ios'
                          ? StyleSheet.hairlineWidth
                          : 1),
                      }}
                    />
                  );
                }
                return null;
              }
              hasOptionAbove = true;
              if (typeof option === 'string') {
                return (
                  <View
                    key={option}
                    style={{
                      padding: theme.paddingHorizontal,
                      width: width || 250,
                      backgroundColor: '#f1f1f1',
                      borderTopWidth: 1,
                      borderBottomWidth: 1,
                      borderColor: '#eaeaea',
                    }}
                  >
                    <Txt size="xxxsmall" weight="bold">
                      {option}
                    </Txt>
                  </View>
                );
              }

              if (React.isValidElement(option)) {
                return option;
              }

              if (!typeGuard<DefinedMenuOption>(option)) {
                return null;
              }

              return (
                <Pressable
                  key={option.key || option.text}
                  style={{
                    padding: theme.paddingHorizontal,
                    width: width || 250,
                    minHeight: 44,
                  }}
                  disabled={disabled || !option.onPress}
                  hoveredStyle={{ backgroundColor: '#f1f1f1' }}
                  pressedStyle={{ backgroundColor: '#dddddd' }}
                  onPress={
                    option.onPress
                      ? () => {
                          if (allowInteraction.current) {
                            if (!option.keepMenuOpenOnPress) {
                              dispatch(eventCloseMenus());
                            }
                            setTimeout(() => {
                              option.onPress?.();
                            }, 1);
                          }
                        }
                      : undefined
                  }
                >
                  <ViewWithSeparators
                    horizontal
                    separatorMargin={theme.paddingHorizontal / 2}
                    separatorColor="transparent"
                    style={{ flex: 1, alignItems: 'center' }}
                  >
                    {option.leftIcon && (
                      <Icon
                        source={option.leftIcon}
                        color={option.leftIconColor}
                        size={12}
                        style={!option.onPress ? { opacity: 0.42 } : undefined}
                      />
                    )}
                    <View
                      style={[
                        { flex: 1 },
                        option.horizontalText
                          ? { flexDirection: 'row', alignItems: 'center' }
                          : undefined,
                      ]}
                    >
                      <Txt
                        size={option.textSize}
                        style={[
                          option.destructive || option.textColor
                            ? {
                                color: option.textColor || theme.colors.red,
                              }
                            : undefined,
                          option.onPress ? undefined : { opacity: 0.42 },
                          option.horizontalText ? { flex: 1 } : { flex: 1 },
                        ]}
                        numberOfLines={
                          option.textNumberOfLines !== undefined
                            ? option.textNumberOfLines
                            : 1
                        }
                      >
                        {reactStringReplace(option.text, bold, (match, i) => (
                          <Txt key={i} weight="bold" size={option.textSize}>
                            {match}
                          </Txt>
                        ))}
                      </Txt>
                      {!!option.subText && (
                        <Txt
                          size={option.horizontalText ? undefined : 'xsmall'}
                          style={[
                            {
                              color: theme.colors.fieldValueText,
                            },
                            option.horizontalText
                              ? { textAlign: 'right' }
                              : { marginTop: 3, flex: 1 },
                          ]}
                        >
                          {reactStringReplace(
                            option.subText,
                            bold,
                            (match, i) => (
                              <Txt key={i} weight="bold">
                                {match}
                              </Txt>
                            ),
                          )}
                        </Txt>
                      )}
                    </View>
                    {option.icon && (
                      <Icon
                        source={option.icon}
                        color={
                          option.iconColor ||
                          (option.destructive ? theme.colors.red : undefined)
                        }
                        style={!option.onPress ? { opacity: 0.42 } : undefined}
                      />
                    )}
                  </ViewWithSeparators>
                </Pressable>
              );
            })}
          </ViewWithSeparators>
        </ScrollView>
      </View>
    </RNMM>
  );
};

const styles = StyleSheet.create({
  menu: {
    borderRadius: 10,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 50,
    backgroundColor: '#fafafa',
  },
  menuDisabled: { opacity: 0.42, pointerEvents: 'none' },
});
